/**
 * Il **cruscotto dei flussi già eseguiti**: quali sezioni sono state collaudate, quanto, e da
 * dove si riprende. Calcoli puri, sotto test; il disegno sta in `CoverageBoard.tsx`.
 *
 * ── Perché serve, e perché sta sulla pagina delle campagne ──
 *
 * La copertura esiste già, ma **dentro** una campagna: per sapere se le Assenze sono state
 * provate bisogna aprire la campagna giusta e scorrere. Chi arriva su «Campagne di collaudo»
 * ha la domanda opposta — *cosa è già stato provato, e dove manca ancora* — e da lì non c'è
 * risposta né strada. Il cruscotto la mette in testa alla pagina e rende ogni sezione un
 * ingresso: un clic apre la checklist della campagna già filtrata su quella sezione.
 *
 * ── Su quale campagna ──
 *
 * Su **una**, non su tutte sommate. Sommare le esecuzioni di campagne diverse mescolerebbe
 * ambienti e versioni: «Assenze provate» diventerebbe vero grazie a una campagna su `dev` di
 * tre mesi fa mentre quella in corso su `staging` non le ha toccate. Si mostra quella attiva
 * più recente, e le altre si scelgono da una tendina.
 */
import {
  MODULE_LABEL,
  type CoverageModuleRow, type CoverageNotExecutedRow, type PlanCoverage, type PlanStatus,
  type TestPlanRow,
} from './api';

/** Ordine di preferenza: chi sta collaudando ora guarda la campagna aperta. */
const STATUS_WEIGHT: Record<string, number> = { active: 0, draft: 1, completed: 2, archived: 3 };

const when = (p: TestPlanRow): number => {
  const t = p.startDate ?? p.createdAt;
  const n = t ? Date.parse(String(t)) : NaN;
  return Number.isFinite(n) ? n : 0;
};

/**
 * La campagna da mostrare all'apertura: attiva prima di tutto, e fra pari la più recente.
 * `null` quando non ce n'è nessuna — e allora il cruscotto non si disegna affatto, perché
 * un cruscotto a zero su zero non dice niente che l'elenco vuoto non dica già.
 */
export function campaignToShow(plans: TestPlanRow[]): TestPlanRow | null {
  return candidateCampaigns(plans)[0] ?? null;
}

/**
 * Le campagne in ordine di preferenza. Il cruscotto parte dalla prima e, se quella non ha
 * flussi nel perimetro, passa alla successiva: una campagna appena creata è quasi sempre
 * vuota, e aprire la pagina trovando un riquadro vuoto è peggio che non avere il riquadro.
 */
export function candidateCampaigns(plans: TestPlanRow[]): TestPlanRow[] {
  return [...plans].sort((a, b) => {
    const pa = STATUS_WEIGHT[String(a.status ?? '')] ?? 9;
    const pb = STATUS_WEIGHT[String(b.status ?? '')] ?? 9;
    return pa - pb || when(b) - when(a);
  });
}

/** L'etichetta con cui una campagna si sceglie dalla tendina: codice, ambiente e versione. */
export function campaignLabel(p: TestPlanRow): string {
  const queue = [p.environment, p.appVersion].filter(Boolean).join(' · ');
  const nome = [p.code, p.name].filter(Boolean).join(' — ');
  return queue ? `${nome} (${queue})` : nome || p.id;
}

export interface SectionRow {
  module: string;
  label: string;
  total: number;
  /** Flussi con almeno un esito: è il numero che risponde a «l'abbiamo provata?». */
  executed: number;
  pass: number;
  fail: number;
  blocked: number;
  skipped: number;
  notRun: number;
  /** 0–100, arrotondata: serve alla barra e al testo, non ai conti. */
  percent: number;
  /** Da guardare per prima: ha fallimenti o è ancora scoperta. */
  daGuardare: boolean;
}

/**
 * Le righe del cruscotto, **in ordine di lavoro che resta**, non alfabetico.
 *
 * Prima le sezioni con fallimenti (sono le uniche che chiedono una decisione), poi quelle
 * più scoperte, poi le altre. Un elenco alfabetico metterebbe «Accessi» in cima anche quando
 * è verde da una settimana, e la sezione con tre fallimenti finirebbe sotto la piega.
 */
export function sectionsRows(coverage: PlanCoverage | null | undefined): SectionRow[] {
  if (!coverage) return [];
  return coverage.byModule
    .map((m): SectionRow => {
      const total = m.total ?? 0;
      const notRun = m.notRun ?? 0;
      const eseguiti = Math.max(0, total - notRun);
      return {
        module: m.module,
        label: MODULE_LABEL[m.module] ?? m.module,
        total,
        executed: eseguiti,
        pass: m.pass ?? 0,
        fail: m.fail ?? 0,
        blocked: m.blocked ?? 0,
        skipped: m.skipped ?? 0,
        notRun,
        percent: total > 0 ? Math.round((eseguiti / total) * 100) : 0,
        daGuardare: (m.fail ?? 0) > 0 || (m.blocked ?? 0) > 0 || notRun > 0,
      };
    })
    .sort((a, b) => (
      (b.fail + b.blocked) - (a.fail + a.blocked)
      || b.notRun - a.notRun
      || a.label.localeCompare(b.label, 'it')
    ));
}

export interface DashboardSummary {
  total: number;
  executed: number;
  percent: number;
  /** Sezioni senza nemmeno un flusso eseguito: quelle mai toccate. */
  intactSections: number;
  /** Sezioni con tutti i flussi eseguiti e nessun fallimento. */
  closedSections: number;
  fail: number;
  blockers: number;
}

export function dashboardSummary(coverage: PlanCoverage | null | undefined, rows: SectionRow[]): DashboardSummary {
  const total = coverage?.total ?? rows.reduce((n, r) => n + r.total, 0);
  const eseguiti = coverage?.executed ?? rows.reduce((n, r) => n + r.executed, 0);
  return {
    total,
    executed: eseguiti,
    /* La percentuale del server quando c'è: la sua l'ha calcolata sull'ultima esecuzione di
       ciascun flusso, e rifarla qui su numeri arrotondati darebbe due valori diversi per la
       stessa cosa nella stessa pagina. */
    percent: coverage?.executedPercent ?? (total > 0 ? Math.round((eseguiti / total) * 100) : 0),
    intactSections: rows.filter((r) => r.executed === 0 && r.total > 0).length,
    closedSections: rows.filter((r) => r.total > 0 && r.notRun === 0 && r.fail === 0 && r.blocked === 0).length,
    fail: rows.reduce((n, r) => n + r.fail, 0),
    blockers: coverage?.blockers ?? 0,
  };
}

/**
 * La frase che accompagna la riga, letta a voce da uno screen reader e mostrata come `title`.
 * È l'unico posto in cui i numeri diventano una cosa che si capisce senza guardare la barra.
 */
export function sectionDescription(r: SectionRow): string {
  if (r.total === 0) return `${r.label}: nessun flusso in questa sezione.`;
  const pieces = [`${r.executed} di ${r.total} flussi eseguiti`];
  if (r.fail > 0) pieces.push(`${r.fail} ${r.fail === 1 ? 'fallito' : 'falliti'}`);
  if (r.blocked > 0) pieces.push(`${r.blocked} ${r.blocked === 1 ? 'bloccato' : 'bloccati'}`);
  if (r.notRun > 0) pieces.push(`${r.notRun} da eseguire`);
  return `${r.label}: ${pieces.join(', ')}.`;
}

/** Vero quando la campagna scelta non ha proprio flussi nel perimetro. */
export const emptyDashboard = (righe: SectionRow[]): boolean =>
  righe.length === 0 || righe.every((r) => r.total === 0);

/**
 * Il filtro `module` accettato dalla checklist della campagna. Si valida contro il vocabolario
 * del catalogo (via `MODULE_LABEL`): un valore inventato nell'URL non deve filtrare via tutto
 * lasciando una pagina vuota che sembra un guasto.
 */
export function validModule(v: unknown): string | undefined {
  if (typeof v !== 'string' || !v.trim()) return undefined;
  const raw = v.trim();
  if (raw in MODULE_LABEL) return raw;
  const lower = raw.toLowerCase();
  return Object.keys(MODULE_LABEL).find((k) => k.toLowerCase() === lower);
}

/** Stato campagna → etichetta breve per il chip accanto al selettore. */
export const campaignStatus = (s?: PlanStatus | string | null): string => String(s ?? '');

// ─────────────────────── La vista generale, su tutte le campagne ───────────────────────

/**
 * La copertura **di tutto il collaudo**, non di una campagna sola.
 *
 * Non si sommano le coperture delle campagne: lo stesso flusso sta in più campagne, e la somma
 * lo conterebbe tre volte facendo salire il totale a ogni campagna aperta. Qui si parte dal
 * **catalogo** — che è l'elenco vero dei flussi — e si guarda l'**ultima esecuzione ovunque**:
 * un flusso passato a luglio e mai più toccato risulta passato, uno fallito ieri risulta
 * fallito anche se a giugno era verde.
 *
 * Torna la stessa forma di `PlanCoverage`, così il cruscotto la disegna senza sapere da dove
 * arriva.
 */
export function generalCoverage(
  cases: { id: string; code?: string; title?: string; module?: string; priority?: string }[],
  runs: { outcome?: string; executedAt?: string; testCase?: { id: string } | null }[],
): PlanCoverage {
  /* Ultima esecuzione per flusso, fra tutte le campagne. `executedAt` mancante vale come
     antichissima: una riga senza data non deve scavalcare una datata. */
  const last = new Map<string, { outcome: string; when: number }>();
  for (const r of runs) {
    const id = r.testCase?.id;
    if (!id) continue;
    const t = r.executedAt ? Date.parse(r.executedAt) : NaN;
    const quando = Number.isFinite(t) ? t : 0;
    const prec = last.get(id);
    if (!prec || quando >= prec.when) last.set(id, { outcome: r.outcome ?? 'not-run', when: quando });
  }

  const outcome = (id: string): string => {
    const u = last.get(id);
    return u && u.outcome ? u.outcome : 'not-run';
  };

  const perModule = new Map<string, CoverageModuleRow>();
  const notExecuted: CoverageNotExecutedRow[] = [];
  for (const c of cases) {
    const m = c.module ?? 'other';
    const row = perModule.get(m) ?? { module: m, total: 0, pass: 0, fail: 0, blocked: 0, skipped: 0, notRun: 0 };
    row.total += 1;
    const o = outcome(c.id);
    if (o === 'pass') row.pass += 1;
    else if (o === 'fail') row.fail += 1;
    else if (o === 'blocked') row.blocked += 1;
    else if (o === 'skipped') row.skipped += 1;
    else {
      row.notRun += 1;
      notExecuted.push({ code: c.code ?? c.id, title: c.title ?? '', module: m, priority: c.priority ?? '' });
    }
    perModule.set(m, row);
  }

  const byModule = [...perModule.values()];
  const total = cases.length;
  const executed = byModule.reduce((n, r) => n + (r.total - r.notRun), 0);
  return {
    total,
    executed,
    executedPercent: total > 0 ? Math.round((executed / total) * 100) : 0,
    /* I bloccanti li sa solo il server (guarda `severity` sull'ultima run): qui resta 0, e la
       vista generale non mostra quel numero invece di mostrarne uno inventato. */
    blockers: 0,
    byModule,
    notExecuted,
  };
}
